/**
 * Tier-1 Admin Growth page (spec §C7).
 *
 * Three tiles per spec: acquisition-by-channel + onboarding-funnel +
 * eligibility-blockers. Backed by GET /v1/admin/analytics/flywheel
 * (getFlywheelMetrics in backend/src/routes/admin.ts).
 *
 * Wire-format `FlywheelMetrics` is inlined here (not imported from
 * @udc/shared) because the admin frontend doesn't bundle the backend
 * package; the contract is pinned by the source-presence lint at
 * tests/unit/admin-growth-wiring-lint.test.ts (mirrors the
 * Launch Command Center pattern at the sibling lint).
 */
import { useCustom } from '@refinedev/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { TrendingUp, UserCheck, AlertTriangle, Users, Heart, DollarSign } from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/Tooltip';
import { StatCardSkeleton, Skeleton } from '@/components/ui/Skeleton';

interface FlywheelMetrics {
  generated_at: string;
  window_days: number;
  channels_7d: Array<{ channel: string; count: number }>;
  onboarding_funnel: {
    signed_up: number;
    signup_wizard_complete: number;
    profile_complete: number;
    hard_filters_complete: number;
    onboarding_complete: number;
    match_ready: number;
  };
  eligibility_blockers: {
    not_started_wizard: number;
    in_wizard: number;
    in_profile: number;
    in_hard_filters: number;
    in_onboarding: number;
  };
}

// GET /v1/admin/analytics/growth-science (getGrowthScience). Live aggregates,
// all real-user-filtered. Fetched separately so a slow/failed science query
// never blocks the core flywheel tiles above.
interface GrowthScience {
  engagement: { dau: number; wau: number; mau: number };
  match_outcomes: Array<{ status: string; count: number }>;
  revenue: Array<{ status: string; count: number; cents: number }>;
  retention_cohorts: Array<{ cohort: string; signups: number; active_w1: number; active_w4: number }>;
}

function pct(n: number, d: number): string {
  if (!d) return '—';
  return `${Math.round((n / d) * 100)}%`;
}

function StatTile({
  title,
  value,
  icon: Icon,
  tooltip,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  tooltip?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            {title}
            {tooltip && <HelpTooltip content={tooltip} />}
          </p>
          <p className="text-2xl font-bold">{formatNumber(value)}</p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
    </div>
  );
}

export function GrowthPage() {
  const { data, isLoading, isError } = useCustom<FlywheelMetrics>({
    url: '/v1/admin/analytics/flywheel',
    method: 'get',
  });

  // Growth Science (engagement / outcomes / revenue / retention) — independent
  // fetch so it degrades on its own without blocking the flywheel tiles.
  const { data: gsData } = useCustom<GrowthScience>({
    url: '/v1/admin/analytics/growth-science',
    method: 'get',
  });
  const gs = gsData?.data;

  const stats = data?.data;

  if (isError) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          Failed to load flywheel metrics. Refresh to retry.
        </div>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="space-y-6 p-6">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-96" />
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const funnel = stats.onboarding_funnel;
  const blockers = stats.eligibility_blockers;
  const notReady = Math.max(0, funnel.signed_up - funnel.match_ready);

  // Funnel rendered as a stacked bar chart (one bar per stage) so
  // operators can eyeball drop-off proportionally rather than reading
  // raw counts.
  const funnelData = [
    { stage: 'Signed up', count: funnel.signed_up },
    { stage: 'Wizard done', count: funnel.signup_wizard_complete },
    { stage: 'Profile', count: funnel.profile_complete },
    { stage: 'Hard filters', count: funnel.hard_filters_complete },
    { stage: 'Onboarding', count: funnel.onboarding_complete },
    { stage: 'Match-ready', count: funnel.match_ready },
  ];

  // Blocker bar chart — concentration of stuck users by earliest
  // incomplete stage. Sums to (signed_up - match_ready); guides
  // operator attention to where the UX needs improvement.
  const blockerData = [
    { stage: 'Not started', count: blockers.not_started_wizard },
    { stage: 'In wizard', count: blockers.in_wizard },
    { stage: 'In profile', count: blockers.in_profile },
    { stage: 'In filters', count: blockers.in_hard_filters },
    { stage: 'In onboarding', count: blockers.in_onboarding },
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Growth</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Acquisition + activation flywheel — last {stats.window_days} days.
          Generated {new Date(stats.generated_at).toLocaleString()}.
        </p>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <StatTile
          title="Signed up"
          value={funnel.signed_up}
          icon={Users}
          tooltip={`New real users created in the last ${stats.window_days} days (excludes fakes and deleted accounts).`}
        />
        <StatTile
          title="Match-ready"
          value={funnel.match_ready}
          icon={UserCheck}
          tooltip="Users with became_match_ready_at set — completed the full onboarding cascade."
        />
        <StatTile
          title="Not-yet-ready"
          value={notReady}
          icon={AlertTriangle}
          tooltip="Signed up in window but haven't reached match-ready. The blocker chart below shows where they're stuck."
        />
      </div>

      {/* Acquisition by channel — last 7d */}
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">
            Acquisition by channel (last 7 days)
          </h2>
          <HelpTooltip content="Channel attribution derived from core.acquisition_touches via the canonical deriveChannel() helper. Same logic as the Launch Command Center channels tile so counts are directly comparable across pages." />
        </div>
        {stats.channels_7d.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No acquisition touches captured in the last 7 days. Either traffic
            hasn't started or the pixel/capture is broken — check the
            data-quality alarm in CloudWatch.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(240, stats.channels_7d.length * 32)}>
            <BarChart data={stats.channels_7d} layout="vertical" margin={{ left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
              <YAxis dataKey="channel" type="category" width={100} stroke="hsl(var(--muted-foreground))" />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem',
                }}
              />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Onboarding funnel — stage cascade */}
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">
            Onboarding funnel ({stats.window_days}-day cohort)
          </h2>
          <HelpTooltip content="Each stage is an independent count of how many users in the 30-day signup cohort have that flag set. Counts are NOT cumulatively subsetted — they show the raw stage population. Drop-off between bars is the % stuck or in-progress at that stage." />
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={funnelData}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis dataKey="stage" stroke="hsl(var(--muted-foreground))" />
            <YAxis stroke="hsl(var(--muted-foreground))" />
            <RechartsTooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '0.375rem',
              }}
            />
            <Bar dataKey="count" fill="hsl(var(--primary))" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Eligibility blockers — where the not-yet-ready cohort is stuck */}
      <div className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">
            Eligibility blockers (where the not-yet-ready cohort is stuck)
          </h2>
          <HelpTooltip content="Each user is bucketed by their EARLIEST incomplete stage. Totals sum to the not-yet-ready count (Signed up − Match-ready). The tallest bar is where UX work has the highest leverage." />
        </div>
        {notReady === 0 ? (
          <p className="text-sm text-muted-foreground">
            All signed-up users in window are match-ready — nothing stuck.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={blockerData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="stage" stroke="hsl(var(--muted-foreground))" />
              <YAxis stroke="hsl(var(--muted-foreground))" />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.375rem',
                }}
              />
              <Bar dataKey="count" fill="hsl(var(--destructive))" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Growth Science (real-user engagement / outcomes / revenue / retention) ── */}
      {gs && (
        <>
          {/* Engagement — DAU/WAU/MAU */}
          <div className="grid grid-cols-3 gap-4">
            <StatTile title="DAU" value={gs.engagement.dau} icon={Users} tooltip="Distinct real users active in the last 24h (last_session_at). Excludes fakes." />
            <StatTile title="WAU" value={gs.engagement.wau} icon={Users} tooltip="Distinct real users active in the last 7 days." />
            <StatTile title="MAU" value={gs.engagement.mau} icon={Users} tooltip="Distinct real users active in the last 30 days." />
          </div>

          {/* Match outcomes — real-involved matches */}
          {(() => {
            const total = gs.match_outcomes.reduce((s, r) => s + r.count, 0);
            const by = (st: string) => gs.match_outcomes.find((r) => r.status === st)?.count ?? 0;
            return (
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Heart className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">Match outcomes (real-involved matches)</h2>
                  <HelpTooltip content="Status distribution of matches involving at least one real user (fake↔fake pairs excluded so the signal isn't swamped). Connect rate = connected / all outcomes." />
                </div>
                <div className="mb-3 flex flex-wrap gap-6 text-sm">
                  <span className="text-muted-foreground">Connect rate: <strong className="text-green-500">{pct(by('connected'), total)}</strong></span>
                  <span className="text-muted-foreground">Mutual rate: <strong className="text-amber-500">{pct(by('mutual'), total)}</strong></span>
                  <span className="text-muted-foreground">Total: <strong className="text-foreground">{formatNumber(total)}</strong></span>
                </div>
                {total === 0 ? (
                  <p className="text-sm text-muted-foreground">No real-involved matches yet.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={gs.match_outcomes}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="status" stroke="hsl(var(--muted-foreground))" />
                      <YAxis stroke="hsl(var(--muted-foreground))" />
                      <RechartsTooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '0.375rem' }} />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            );
          })()}

          {/* Revenue — match payments */}
          {(() => {
            const completed = gs.revenue.find((r) => r.status === 'completed');
            const cents = completed?.cents ?? 0;
            const count = completed?.count ?? 0;
            return (
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">Revenue (match payments)</h2>
                  <HelpTooltip content="Realized revenue = completed match_payments (the $25 connect fee is the only paywall today). Non-completed statuses shown alongside." />
                </div>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-muted/50 p-4">
                    <div className="text-2xl font-bold">${(cents / 100).toLocaleString()}</div>
                    <div className="text-sm text-muted-foreground">Realized revenue</div>
                  </div>
                  <div className="rounded-lg bg-muted/50 p-4">
                    <div className="text-2xl font-bold">{formatNumber(count)}</div>
                    <div className="text-sm text-muted-foreground">Completed payments</div>
                  </div>
                  {gs.revenue
                    .filter((r) => r.status !== 'completed')
                    .map((r) => (
                      <div key={r.status} className="rounded-lg bg-muted/50 p-4">
                        <div className="text-2xl font-bold">{formatNumber(r.count)}</div>
                        <div className="text-sm capitalize text-muted-foreground">{r.status}</div>
                      </div>
                    ))}
                </div>
              </div>
            );
          })()}

          {/* Retention cohorts — weekly */}
          <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Retention cohorts (weekly, real users)</h2>
              <HelpTooltip content="Each row is a signup week. W1 / W4 = % of that cohort with activity ≥7 / ≥28 days after signup. Trailing 8 weeks." />
            </div>
            {gs.retention_cohorts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No real-user signups in the last 8 weeks.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Cohort week</th>
                      <th className="py-2 pr-4 font-medium">Signups</th>
                      <th className="py-2 pr-4 font-medium">W1 retained</th>
                      <th className="py-2 font-medium">W4 retained</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gs.retention_cohorts.map((c) => (
                      <tr key={c.cohort} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium text-foreground">{c.cohort}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{c.signups}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {c.active_w1} <span className="text-xs">({pct(c.active_w1, c.signups)})</span>
                        </td>
                        <td className="py-2 text-muted-foreground">
                          {c.active_w4} <span className="text-xs">({pct(c.active_w4, c.signups)})</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
